import { type Component, createSignal, onMount, Show } from "solid-js";
import { Portal } from "solid-js/web";
import NoSaveIcon from "~/Icons/32_Icon_NoSave.svg?component-solid";
import Editor from "~/js/editor";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { clearWorkspace, collectWorkspaceData, showSavedModal } from "~/js/workspace";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { WorkspaceApi } from "~/solidJs/entities/WorkspaceListEntity/api/WorkspaceApi";
import {
	getWorkspacesStore,
	setIsWorkspaceLimitModalVisible,
	setWorkspacesStore
} from "~/solidJs/entities/WorkspaceListEntity/models/wrokspaceListContext";
import { LayoutSaveProjectFeature } from "~/solidJs/features/HeaderFeatures/SaveProjectFeature";
import {
	getIsSaveFunctionalityAvailable,
	getPrivateMessageFromError,
	getPublicMessageFromError
} from "~/solidJs/shared/helpers";
import { useSaveWorkspaceFeature } from "~/solidJs/shared/hooks/useSaveWorkspaceFeature";
import {
	getIsAproveLeaveVisible,
	getIsClearWorkspaceVisible,
	getIsMissingWorkspaceByLinkVisible,
	getIsSaveNewProjectVisible,
	setIsAproveLeaveVisible,
	setIsClearWorkspaceVisible,
	setIsMissingWorkspaceByLinkVisible,
	setIsSaveNewProjectVisible
} from "~/solidJs/shared/models/globalStores";
import { openPricingPage } from "~/solidJs/shared/models/pageParams/pricingPageParams";
import { Button, Input, InputLabelError, ModalV2Ui } from "~/solidJs/shared/ui";
import { showToastMessage } from "../WorkspaceToast/WorkspaceToast";
export type SaveNewProjectModalStore =
	| {
			isVisible: false;
	  }
	| {
			isVisible: true;
			callback?: () => void;
			cancelCallback?: () => void;
			initialName?: string;
	  };
type SaveNewProjectModalProps = Record<string, never>;

export const [getSaveCurrentProjectStore, setSaveCurrentProjectStore] = createSignal<{
	isVisible: boolean;
	onCloseCallback: null | (() => void);
}>({
	isVisible: false,
	onCloseCallback: null
});

export const ClearWorkspaceModal: Component = () => {
	const saveFeature = useSaveWorkspaceFeature();

	const onSaveProject = () => {
		EventAnalytics.saveWorkspaceBtnClick();
		const canSave = getIsSaveFunctionalityAvailable(getUserStore());
		setIsClearWorkspaceVisible(false);
		if (!canSave) {
			openPricingPage({ source: "clearWorkspaceModal" });
			return;
		}
		saveFeature.save();
	};

	const onClose = () => {
		setIsClearWorkspaceVisible(false);
	};

	return (
		<Show when={getIsClearWorkspaceVisible()}>
			<Portal mount={document.body}>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					showCloseButton
					autoClose
					toggleSelf={onClose}
				>
					<form
						onSubmit={(e) => {
							e.preventDefault();
						}}
						class="relative"
					>
						<ModalV2Ui.ContentContainerText>
							<h1 class="header-30-semibold text-text-primary">Clear workspace</h1>
							<p class="typography-text-14-medium text-text-secondary-v2">
								Are you sure you want to delete all nodes and results?
							</p>
						</ModalV2Ui.ContentContainerText>

						<ModalV2Ui.ButtonContainer>
							<Button
								onClick={() => {
									setIsClearWorkspaceVisible(false);
									clearWorkspace();
								}}
								variant="TertiaryAccent"
								size="large"
							>
								Clear Workspace
							</Button>
							<Button
								pending={saveFeature.getIsPending()}
								disabled={saveFeature.getIsDisabled()}
								onClick={onSaveProject}
								variant="PrimaryAccent"
								size="large"
							>
								Save project
							</Button>
						</ModalV2Ui.ButtonContainer>
					</form>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

export const SaveCurrentProject: Component = () => {
	const onClose = () => {
		setSaveCurrentProjectStore((prev) => ({ ...prev, isVisible: false }));
	};
	return (
		<Show when={getSaveCurrentProjectStore().isVisible}>
			<Portal mount={document.body}>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					showCloseButton
					autoClose
					toggleSelf={onClose}
				>
					<form
						onSubmit={(e) => {
							e.preventDefault();
						}}
						class="relative"
					>
						<ModalV2Ui.ContentContainerText>
							<h1 class="header-30-semibold text-text-primary">Unsaved changes</h1>
							<p class="typography-text-14-medium text-text-secondary-v2">
								If you proceed without saving, your current changes in project will
								be lost.
							</p>
						</ModalV2Ui.ContentContainerText>
						<ModalV2Ui.ButtonContainer>
							<Button
								data-test={"discard-changes"}
								onClick={() => {
									getSaveCurrentProjectStore().onCloseCallback?.();
									setSaveCurrentProjectStore((_prev) => ({
										onCloseCallback: null,
										isVisible: false
									}));
								}}
								variant="TertiaryAccent"
								size="large"
							>
								Discard changes
							</Button>
							<LayoutSaveProjectFeature
								afterSaveCallback={() => {
									getSaveCurrentProjectStore().onCloseCallback?.();
									setSaveCurrentProjectStore((_prev) => ({
										onCloseCallback: null,
										isVisible: false
									}));
								}}
								size="large"
								variant={"PrimaryAccent"}
							/>
						</ModalV2Ui.ButtonContainer>
					</form>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

export const MissWorkspaceByLinkModal: Component = () => {
	const onClose = () => {
		setIsMissingWorkspaceByLinkVisible(false);
	};
	return (
		<Show when={getIsMissingWorkspaceByLinkVisible()}>
			<Portal mount={document.body}>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					showCloseButton
					autoClose
					toggleSelf={onClose}
				>
					<form
						onSubmit={(e) => {
							e.preventDefault();
						}}
						class="relative"
					>
						<ModalV2Ui.ContentContainerText>
							<h1 class="header-30-semibold text-text-primary">
								This project is no longer available
							</h1>
						</ModalV2Ui.ContentContainerText>
						<ModalV2Ui.ButtonContainer>
							<Button
								data-test={"discard-changes"}
								onClick={onClose}
								variant="TertiaryAccent"
								size="large"
							>
								Close
							</Button>
						</ModalV2Ui.ButtonContainer>
					</form>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

export const ApproveLeaveModal: Component = () => {
	const saveFeature = useSaveWorkspaceFeature();
	const onSaveProject = () => {
		EventAnalytics.saveWorkspaceBtnClick();
		const canSave = getIsSaveFunctionalityAvailable(getUserStore());
		setIsAproveLeaveVisible(false);
		if (!canSave) {
			openPricingPage({ source: "clearWorkspaceModal" });
			return;
		}
		saveFeature.save();
	};
	const onCancel = () => {
		const state = getIsAproveLeaveVisible();
		if (!state) {
			return;
		}
		Editor.context.graph.toggleIsSavedState(true);
		state.callback?.();
		setIsAproveLeaveVisible(false);
	};
	const onClose = () => {
		setIsAproveLeaveVisible(false);
	};
	return (
		<Show when={getIsAproveLeaveVisible()}>
			<Portal mount={document.body}>
				<ModalV2Ui.ModalWithOverlayBoilerPlate autoClose toggleSelf={onClose}>
					<ModalV2Ui.ContentContainerText>
						<div class="flex gap-2">
							<NoSaveIcon />
							<h1 class="header-30-semibold text-text-primary">Unsaved changes</h1>
						</div>
						<p class="typography-text-14-medium text-text-secondary-v2">
							Are you sure you want to leave? Changes you made will not be saved
						</p>
					</ModalV2Ui.ContentContainerText>
					<ModalV2Ui.ButtonContainer>
						<Button
							variant="TertiaryAccent"
							size="large"
							onClick={() => {
								onCancel();
							}}
						>
							Don’t save & Exit
						</Button>
						<Button
							size="large"
							disabled={saveFeature.getIsDisabled()}
							pending={saveFeature.getIsPending()}
							onClick={onSaveProject}
							variant="PrimaryAccent"
						>
							Save
						</Button>
					</ModalV2Ui.ButtonContainer>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

export const SaveNewProjectModal: Component<SaveNewProjectModalProps> = (_props) => {
	let inputRef: HTMLInputElement | undefined;
	const [getNameState, setNameState] = createSignal("");
	const [getIsPending, setIsPending] = createSignal(false);
	const [getError, setError] = createSignal("");
	const createNewProjectHandler = async () => {
		const config = getIsSaveNewProjectVisible();
		const inputedName = getNameState().trim();
		if (!config.isVisible || !inputedName) {
			setError("Please write a name for your project");
			return;
		}
		const canSave = getIsSaveFunctionalityAvailable(getUserStore());
		if (!canSave) {
			openPricingPage({ source: "saveNewProjectBtn" });
			config.cancelCallback?.();
			return;
		}
		setError("");
		setIsPending(true);
		const { projectsAmount } = getWorkspacesStore();
		const user = getUserStore()!;
		if (projectsAmount >= user?.workspace_limit || 0) {
			config.cancelCallback?.();
			setIsWorkspaceLimitModalVisible(true);
			setIsPending(false);
			return;
		}
		try {
			const { jsonData, fileIds } = await collectWorkspaceData();

			const { id, name } = await WorkspaceApi.postWorkspaceWithFileMove(
				{
					config: jsonData,
					uploaded_file_ids: fileIds
				},
				inputedName
			);
			setWorkspacesStore((prev) => ({
				projectsAmount: prev.projectsAmount + 1,
				activeProjectId: id,
				activeProjectName: name,
				activeWorkspaceLink: null,
				isOpenedByLink: false,
				isStateSaved: true,
				isOpenPending: false
			}));

			showSavedModal();
		} catch (e) {
			console.error(e);
			EventAnalytics.sendErrorInfo("create-new-project-handler-fc", {
				message: getPrivateMessageFromError(e) || "create-new-project-handler-fc"
			});
			showToastMessage({
				type: "error" as any,
				title: `Error during saving workspace: ${getPublicMessageFromError(e) || "Unkown error"}`,
				timeout: 2000
			});
		}
		Editor.context.graph.toggleIsSavedState(true);
		setIsSaveNewProjectVisible({ isVisible: false });
		setIsPending(false);
		config.callback?.();
	};
	const closeModal = () => {
		const config = getIsSaveNewProjectVisible();
		if (!config.isVisible) return;
		setIsSaveNewProjectVisible({ isVisible: false });
		config.cancelCallback?.();
	};
	return (
		<Show when={getIsSaveNewProjectVisible().isVisible} keyed>
			{(_isPremiumNnsModalVisible) => {
				onMount(() => {
					inputRef?.focus();
					const config = getIsSaveNewProjectVisible();
					setNameState((config.isVisible && config.initialName) || "");
				});
				return (
					<Portal mount={document.body}>
						<ModalV2Ui.ModalWithOverlayBoilerPlate
							showCloseButton
							autoClose
							toggleSelf={closeModal}
						>
							<form
								onSubmit={(e) => {
									e.preventDefault();
								}}
								class="relative"
							>
								<ModalV2Ui.ContentContainerInput>
									<ModalV2Ui.ContentContainerTextWithoutPadding>
										<h1 class="header-30-semibold text-text-primary">
											Name your project
										</h1>
										<p class="typography-text-14-medium text-text-secondary-v2">
											Please name your project to save changes
										</p>
									</ModalV2Ui.ContentContainerTextWithoutPadding>
									<InputLabelError
										errorMessage={getError()}
										id="save-new-project-input"
									>
										<Input
											placeholder="Project name"
											value={getNameState()}
											variant="Gray"
											ref={inputRef}
											size="medium"
											id="save-new-project-input"
											data-test="save-new-project-input"
											onInput={(e) => {
												const trimmedValue = e.currentTarget.value.slice(
													0,
													200
												);
												e.currentTarget.value = trimmedValue;
												setNameState(trimmedValue);
											}}
										/>
									</InputLabelError>
								</ModalV2Ui.ContentContainerInput>
								<ModalV2Ui.ButtonContainer>
									<Button
										data-test="save-new-project-cancel-btn"
										variant="TertiaryAccent"
										size="large"
										pending={getIsPending()}
										type="button"
										onClick={() => {
											closeModal();
										}}
									>
										Cancel
									</Button>
									<Button
										data-test="save-new-project-btn"
										variant="PrimaryAccent"
										disabled={!getNameState().length}
										size="large"
										type="submit"
										onClick={() => {
											createNewProjectHandler();
										}}
										pending={getIsPending()}
									>
										Save
									</Button>
								</ModalV2Ui.ButtonContainer>
							</form>
						</ModalV2Ui.ModalWithOverlayBoilerPlate>
					</Portal>
				);
			}}
		</Show>
	);
};
